import * as React from "react"

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId: string) => void
      getResponse: (widgetId: string) => string | undefined
    }
  }
}

function ContactForm() {
  const [sending, setSending] = React.useState(false)
  const [sent, setSent] = React.useState(false)
  const [formMessage, setFormMessage] = React.useState("")
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({})
  const [turnstileToken, setTurnstileToken] = React.useState("")
  const form = React.useRef<HTMLFormElement>(null)
  const turnstileContainer = React.useRef<HTMLDivElement>(null)
  const widgetId = React.useRef<string | null>(null)

  React.useEffect(() => {
    const renderWidget = () => {
      if (!turnstileContainer.current || widgetId.current) return
      widgetId.current = window.turnstile!.render(turnstileContainer.current, {
        sitekey: "0x4AAAAAADrWPYGO09LJH7AV",
        theme: "dark",
        callback: (token: string) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      })
    }

    if (window.turnstile) {
      renderWidget()
      return
    }

    const poll = setInterval(() => {
      if (window.turnstile) {
        clearInterval(poll)
        renderWidget()
      }
    }, 100)

    return () => clearInterval(poll)
  }, [])

  const resetTurnstile = () => {
    setTurnstileToken("")
    if (widgetId.current) window.turnstile?.reset(widgetId.current)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!form.current?.reportValidity()) return

    if (!turnstileToken) {
      setFormMessage("Please complete the security check before submitting.")
      return
    }

    setSending(true)
    setFieldErrors({})
    setFormMessage("")

    const formData = new FormData(form.current!)
    const params = new URLSearchParams()
    for (const [key, value] of formData.entries()) {
      params.append(key, value as string)
    }
    params.set("cf-turnstile-response", turnstileToken)
    params.set("website", "") // ensure honeypot is always empty — browsers autocomplete name="website"

    try {
      const res = await fetch("https://email-worker.mattdoesdev.workers.dev", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      })
      const json = await res.json()

      if (json.success) {
        setFormMessage("Your message was sent! Thank you.")
        form.current?.reset()
        setSent(true)
        resetTurnstile()
      } else if (json.fields) {
        setFieldErrors(json.fields)
        resetTurnstile()
      } else {
        setFormMessage(json.error || "There was an error sending your message. Please try again later.")
        resetTurnstile()
      }
    } catch {
      setFormMessage("There was an error sending your message. Please try again later.")
      resetTurnstile()
    } finally {
      setSending(false)
    }
  }

  if (sent) {
    return (
      <div className="pt-5 lg:pt-20 pb-10">
        <p className="font-semibold text-white">{formMessage}</p>
      </div>
    )
  }

  return (
    <form ref={form} onSubmit={handleSubmit}>
        {/* honeypot — visible to bots, hidden from humans */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
        />

        <input
          name="name"
          type="text"
          placeholder="NAME"
          className="contact-form-input mb-8"
          required
        />
        {fieldErrors.name && <p className="text-red-400 text-sm -mt-6 mb-6">{fieldErrors.name}</p>}

        <input
          name="phone"
          type="tel"
          placeholder="PHONE"
          className="contact-form-input mb-8"
        />

        <input
          name="email"
          type="email"
          placeholder="EMAIL"
          className="contact-form-input mb-8"
          required
        />
        {fieldErrors.email && <p className="text-red-400 text-sm -mt-6 mb-6">{fieldErrors.email}</p>}

        <textarea
          rows={5}
          name="message"
          className="contact-form-textarea mb-8"
          placeholder="MESSAGE"
          required
        />
        {fieldErrors.message && <p className="text-red-400 text-sm -mt-6 mb-6">{fieldErrors.message}</p>}

        <div className="mb-8">
          <div ref={turnstileContainer} />
        </div>

        <input
          type="submit"
          className="bg-transparent cursor-pointer hover:text-secondary transition-colors uppercase text-white font-bold disabled:opacity-50"
          value={sending ? "Sending..." : "Send Now"}
          disabled={sending}
        />

        {formMessage && <p className="text-white mt-4">{formMessage}</p>}
      </form>
  )
}

export default ContactForm
